import { chartDurationSeconds, type Chart } from '@shared/chart';
import type { LoopSpeedRamp } from '@shared/progression';
import { HAZARD_INVULN_BEATS } from '@shared/gameTypes';
import {
  scheduleHitClick,
  scheduleFlipWhoosh,
  scheduleDeathSound,
  scheduleLeadInTick,
  scheduleComboCelebration,
  scheduleComboBreakBoom,
  scheduleMashTap,
  scheduleMilestoneFanfare,
  scheduleVictorySting,
  scheduleFailureSting,
  scheduleScoreTick,
  scheduleWavesThrust,
  scheduleWavesDive,
  type HitClickOptions,
} from './sfx';
import {
  scheduleCrashBoom,
  scheduleGuitarHitLayers,
  type GuitarHitLayersOptions,
} from './guitarSfx';
import { scheduleCirclesHitsounds } from './circlesSfx';
import type { HitSampleSet } from '@shared/circlesChart';
import { scheduleGuitarMilestoneFanfare } from './guitarMilestoneSfx';
import { GuitarHoldDrone, type GuitarHoldDroneOptions } from './guitarHoldDrone';
import { GuitarStageBus } from './guitarStageBus';
import { fetchAndDecodeAudio } from './bufferCache';
import { enablePlaybackAudioSession } from './iosAudioSession';
import {
  songTimeAt,
  speedAt,
  wallTimeAt,
  type LoopSpeedClock,
} from './loopSpeedClock';

// Cap on simultaneously-playing one-shot SFX (flip whooshes, hit clicks).
// Without this, mashing Space spawns dozens of overlapping AudioNodes per
// frame which compounds into garbled noise + GC pauses.
const MAX_SFX_VOICES = 6;

/**
 * Cutoff (Hz) the music lowpass filter sits at when no sweep is
 * active. Picked just below the typical 48 kHz Nyquist so the filter
 * is acoustically transparent — the music sounds identical to a
 * direct `musicBus → master` path until {@link AudioEngine.sweepMusicLowpass}
 * fires.
 */
const MUSIC_LOWPASS_NEUTRAL_HZ = 20000;

// Lower bound on the audio source's playbackRate. There is deliberately no
// upper bound: infinite mode keeps stepping the requested speed up and we let
// it climb without limit, so both the music and the gameplay clock (which
// integrates this same rate) accelerate every loop. Past ~4× the resampled
// audio pitches up into chipmunk/aliasing territory and the chart eventually
// outpaces human reaction — that endgame degradation is intended, not
// something we clamp away. The floor only guards against a non-positive rate,
// which would break the AudioParam ramp and the `÷ speed` lead-in math.
export const MIN_PLAYBACK_RATE = 0.25;

export function clampPlaybackRate(value: number): number {
  return Math.max(MIN_PLAYBACK_RATE, value);
}

/**
 * Per-frame ceiling on how far a session's engine clock may advance, in
 * chart beats at 1× playback. {@link AudioEngine.maxAdvanceBeatsPerFrame}
 * scales this by the live playback rate so accelerated infinite-mode loops
 * aren't throttled to the loop-1 step size. It bounds a stall/tab-resume
 * catch-up to a fixed wall-time burst (~0.25s at 120 BPM) rather than
 * processing a huge skipped window — and its missed-pad fallout — in a
 * single frame.
 */
const MAX_ADVANCE_BEATS_BASE = 0.5;

type TimeoutId = ReturnType<typeof setTimeout>;

export interface AudioEngineOptions {
  audioOffsetMs: number;
}

/**
 * Drives audio for a single play session.
 *
 * Music is now an audio file (one per chart) decoded into an AudioBuffer
 * and played via a single `AudioBufferSourceNode`. The chart clock is
 * derived from `audioContext.currentTime - songStartTime` rather than
 * scheduled note-by-note - which means timing matches what the player
 * literally hears, no drift, no cumulative scheduler error.
 *
 * The SFX bus is a separate gain node so the music slider and SFX slider
 * are independent. Pause / resume work via `ctx.suspend / ctx.resume`,
 * which freezes both buses simultaneously.
 */
export class AudioEngine {
  private ctx: AudioContext;
  private master: GainNode;
  private musicBus: GainNode;
  private sfxBus: GainNode;
  private songStartTime = 0;
  private bpm = 120;
  /**
   * Music playback speed schedule for the active loop. Loop 1 holds at
   * 1.0×; sped-up loops sweep from `startSpeed` to `endSpeed` linearly
   * in wall time over `loopWallDuration`, applied to the source's
   * {@link AudioBufferSourceNode.playbackRate} via
   * {@link AudioParam.linearRampToValueAtTime}. The chart clock in
   * {@link getSongTime}/{@link getCurrentBeat} integrates that same
   * ramp so chart logic and audio stay in lockstep through the loop;
   * see `loopSpeedClock.ts` for the math.
   */
  private speedClock: LoopSpeedClock = {
    startSpeed: 1.0,
    endSpeed: 1.0,
    loopWallDuration: Infinity,
  };
  private opts: AudioEngineOptions;
  private started = false;
  private musicSource: AudioBufferSourceNode | null = null;
  /**
   * Tracks active "voices" so we can cap concurrent SFX. We also retain a
   * Set of release timers so stop() can clear them - leaving them dangling
   * leaks the timer queue and keeps mutating activeSfx after teardown.
   */
  private activeSfx = 0;
  private pendingSfxReleases = new Set<TimeoutId>();
  /**
   * Outstanding setTimeout id for the post-fade music source disconnect
   * scheduled by stopMusic(). Tracked so destroy()/stop() can cancel it,
   * preventing a fire-after-context-close warning if the player navigates
   * away during the fade tail.
   */
  private musicFadeTimer: TimeoutId | null = null;
  private musicLoadError: string | null = null;
  private scheduleErrors = 0;
  /**
   * Per-mode stage-bus splice (compressor + soft saturator + reverb
   * send) between the SFX bus and master. `null` until
   * {@link enableStageBus} is called — Piano + Circles never turn it
   * on so their SFX bus stays bone dry.
   */
  private guitarStageBus: GuitarStageBus | null = null;
  /**
   * Lazily-spliced biquad sitting between {@link musicBus} and
   * {@link master}. `null` for non-Guitar modes so Piano + Circles +
   * Pulse's music chain stays at its original single-node topology;
   * {@link enableStageBus} builds + splices it for Guitar so
   * {@link sweepMusicLowpass} can dip its cutoff for the "amp blew
   * out" multi-miss crash effect.
   */
  private musicLowpass: BiquadFilterNode | null = null;
  /**
   * Active Guitar hold-drone voices keyed by lane (0–3). Maintained
   * via {@link startGuitarHoldDrone} / {@link stopGuitarHoldDrone};
   * cleared on {@link clearGuitarAudioFx} and {@link stop}. Capped
   * implicitly at 4 by the lane key (only one drone per lane at a
   * time).
   */
  private guitarHoldDrones: Map<number, GuitarHoldDrone> = new Map();

  constructor(opts: AudioEngineOptions) {
    this.opts = opts;
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) {
      throw new Error('Web Audio API not supported in this browser');
    }
    this.ctx = new Ctx({ latencyHint: 'interactive' });
    this.master = this.ctx.createGain();
    this.musicBus = this.ctx.createGain();
    this.sfxBus = this.ctx.createGain();
    this.master.gain.value = 1.0;
    this.musicBus.gain.value = 1.0;
    this.sfxBus.gain.value = 1.0;
    this.musicBus.connect(this.master);
    this.sfxBus.connect(this.master);
    this.master.connect(this.ctx.destination);
  }

  get audioContext(): AudioContext {
    return this.ctx;
  }

  get currentTime(): number {
    return this.ctx.currentTime;
  }

  get isStarted(): boolean {
    return this.started;
  }

  /**
   * Test/debug accessor. Production code does not read this. Surfaces
   * the most-recent music-load failure so a regression test can assert
   * the audio file actually decoded.
   */
  get debugMusicLoadError(): string | null {
    return this.musicLoadError;
  }

  /**
   * Number of active SFX voices, exposed for tests asserting the cap.
   */
  get debugActiveSfxVoices(): number {
    return this.activeSfx;
  }

  /**
   * Total Web Audio scheduling failures since this engine was constructed.
   * Stays at 0 unless something unusual (suspended context, missing buffer)
   * trips a SFX scheduler. Surfaced for diagnostics; production never reads it.
   */
  get debugScheduleErrorCount(): number {
    return this.scheduleErrors;
  }

  async resume(): Promise<void> {
    // Declare playback intent before unlocking so iOS/iPadOS doesn't route this
    // through the silent-switch-muted "ambient" session. Set on every resume
    // (cheap, idempotent) so a backgrounding interruption can't leave us muted.
    enablePlaybackAudioSession();
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch (err) {
        // Some browsers (esp. Safari and locked-down Chromium) reject
        // resume() if the call wasn't synchronous-enough to a user
        // gesture. Surface the error so the caller can show a useful
        // message instead of silently hanging on a dead audio graph.
        throw new Error(
          `AudioContext.resume rejected (${(err as Error).message ?? 'unknown'}). ` +
            'The browser likely blocked autoplay - try clicking Start again.',
        );
      }
    }
  }

  async suspend(): Promise<void> {
    if (this.ctx.state === 'running') {
      await this.ctx.suspend();
    }
  }

  /**
   * Whether the audio context is currently suspended (paused). Used by the
   * session to decide whether to advance the engine clock.
   */
  isSuspended(): boolean {
    return this.ctx.state === 'suspended';
  }

  setMusicVolume(v: number): void {
    this.musicBus.gain.value = Math.max(0, Math.min(1, v));
  }

  setSfxVolume(v: number): void {
    this.sfxBus.gain.value = Math.max(0, Math.min(1, v));
  }

  setMasterVolume(v: number): void {
    this.master.gain.value = Math.max(0, Math.min(1, v));
  }

  setAudioOffsetMs(ms: number): void {
    this.opts.audioOffsetMs = ms;
  }

  /**
   * Start the song. Decodes the chart's audio file (using a process-wide
   * cache so retries are instant), then schedules a single buffer source
   * to begin at `now + 0.1s + leadInBeats + userOffset`. The 0.1s headroom
   * absorbs scheduler jitter so the very first kick sample isn't clipped.
   *
   * `audio.offsetMs` is the in-file silence/intro that happens before the
   * chart's beat 0 - so we play the buffer from `audio.offsetMs/1000`s in,
   * not from the start of the file.
   */
  async start(
    chart: Chart,
    leadInBeats = 4,
    opts: { speedRamp?: LoopSpeedRamp; fileOffsetSeconds?: number } = {},
  ): Promise<void> {
    if (this.started) {
      // Calling start() twice would leak the previous BufferSource, leave
      // it audibly playing alongside a new one, and overwrite songStartTime
      // so the chart clock jumps. The session lifecycle should already
      // prevent this; throwing makes the contract explicit if it ever
      // doesn't.
      throw new Error('AudioEngine.start: already started; call stop() before starting again');
    }
    await this.resume();
    this.bpm = chart.bpm;
    this.applySpeedRamp(chart, opts.speedRamp);

    let buffer: AudioBuffer;
    try {
      buffer = await fetchAndDecodeAudio(this.ctx, chart.audio.src);
      this.musicLoadError = null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.musicLoadError = msg;
      console.warn('AudioEngine: failed to load music for chart', chart.slug, err);
      // We still mark the engine as started and run silently rather than
      // throw - the rest of the session machinery (timing clock, hit SFX,
      // visuals) all keys off `started`. The session UI surfaces the
      // music-load error separately so the player knows what happened.
      const userOffsetSeconds = this.opts.audioOffsetMs / 1000;
      const leadInSeconds = this.computeLeadInSeconds(leadInBeats, chart.bpm);
      this.songStartTime = this.ctx.currentTime + 0.1 + leadInSeconds + userOffsetSeconds;
      this.started = true;
      return;
    }

    const userOffsetSeconds = this.opts.audioOffsetMs / 1000;
    const leadInSeconds = this.computeLeadInSeconds(leadInBeats, chart.bpm);
    // Start of beat 0 in audio-context time. The buffer source itself is
    // scheduled at the same instant but with `fileOffsetSeconds` of in-file
    // skip applied so the first audible sample lines up with beat 0.
    this.songStartTime = this.ctx.currentTime + 0.1 + leadInSeconds + userOffsetSeconds;

    // Lets the loop manager start a repeat from a non-zero point in the
    // file (e.g. skip a long intro on the second loop) without changing
    // the chart-side beat 0 reference.
    const baseFileOffsetSeconds = Math.max(0, chart.audio.offsetMs / 1000);
    const fileOffsetSeconds = baseFileOffsetSeconds + (opts.fileOffsetSeconds ?? 0);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    this.scheduleSourceSpeedRamp(source);
    source.connect(this.musicBus);
    source.start(this.songStartTime, fileOffsetSeconds);
    this.musicSource = source;
    this.started = true;
  }

  /**
   * Apply a fresh speed ramp for a new loop. Floors each endpoint to a
   * positive playback rate and recomputes the loop's wall duration so
   * the integrated song-time at the loop end equals the chart's
   * natural duration.
   */
  private applySpeedRamp(chart: Chart, ramp: LoopSpeedRamp | undefined): void {
    const startSpeed = clampPlaybackRate(ramp?.startSpeed ?? 1);
    const endSpeed = clampPlaybackRate(ramp?.endSpeed ?? startSpeed);
    // L_wall * (start + end) / 2 == chartDurationSec, so L_wall scales
    // by the average rate. With a flat ramp (loop 1) this collapses to
    // chartDurationSec / startSpeed.
    const chartDurationSec = chartDurationSeconds(chart.bpm, chart.totalBeats);
    const avgSpeed = (startSpeed + endSpeed) / 2;
    this.speedClock = {
      startSpeed,
      endSpeed,
      loopWallDuration: chartDurationSec / avgSpeed,
    };
  }

  /**
   * Lead-in seconds for `beats` chart-beats. The lead-in is played
   * before the song starts, so the audio source's playbackRate ramp
   * hasn't begun — the lead-in always runs at the loop's start speed.
   */
  private computeLeadInSeconds(leadInBeats: number, bpm: number): number {
    return (leadInBeats * 60) / bpm / this.speedClock.startSpeed;
  }

  /**
   * Hook the loop's speed schedule onto the freshly-created
   * `AudioBufferSourceNode`. A flat ramp (loop 1) just pins the value;
   * a sped-up loop schedules a linear ramp so the audio accelerates
   * smoothly across the full loop wall duration.
   */
  private scheduleSourceSpeedRamp(source: AudioBufferSourceNode): void {
    const { startSpeed, endSpeed, loopWallDuration } = this.speedClock;
    const param = source.playbackRate;
    param.setValueAtTime(startSpeed, this.songStartTime);
    if (endSpeed !== startSpeed) {
      param.linearRampToValueAtTime(
        endSpeed,
        this.songStartTime + loopWallDuration,
      );
    }
  }

  /**
   * Song-time elapsed since beat 0 (negative during lead-in). Integrates
   * the active speed ramp so the chart clock advances at the same pace
   * the audio source is consuming its buffer. See `loopSpeedClock.ts`
   * for the math.
   */
  getSongTime(): number {
    if (!this.started) return -Infinity;
    return songTimeAt(this.speedClock, this.ctx.currentTime - this.songStartTime);
  }

  getCurrentBeat(): number {
    if (!this.started) return -Infinity;
    return this.getSongTime() * (this.bpm / 60);
  }

  /**
   * Inverse of {@link getCurrentBeat}: ctx-time at which the given
   * song-beat occurs. Valid for any beat the engine asks about — the
   * `loopSpeedClock` inverse covers lead-in, the linear-ramp interior,
   * and the post-ramp tail. Used by {@link scheduleLeadInTicks} to
   * time the count-in blips at constant start speed.
   */
  beatToCtxTime(beat: number): number {
    const targetSongTime = (beat * 60) / this.bpm;
    return this.songStartTime + wallTimeAt(this.speedClock, targetSongTime);
  }

  /**
   * Instantaneous playback rate at `ctx.currentTime`. Used by tests
   * asserting the ramp applied; production reads via the chart clock.
   */
  getPlaybackRate(): number {
    if (!this.started) return this.speedClock.startSpeed;
    return speedAt(this.speedClock, this.ctx.currentTime - this.songStartTime);
  }

  /**
   * Per-frame cap on engine-clock advance, in beats. Scales with the live
   * playback rate so accelerated loops keep pace with the audio (a fixed cap
   * pegs gameplay at the loop-1 speed and desyncs it from the music), but is
   * itself capped at {@link HAZARD_INVULN_BEATS}: a single engine step must
   * not span a full hazard-invulnerability period, or Pulse's once-per-step
   * hazard sampling would apply fewer hits than the server's fine-grained
   * (1/16-beat) replay re-sim and reject a legit run as a mismatch. Gameplay
   * therefore stops accelerating once the speed-scaled step hits that ceiling
   * (~30× at 60 fps / 120 BPM — far past playable). `Math.max(1, …)` floors
   * the multiplier so a sub-1× playback rate can't shrink the cap below the
   * well-tested loop-1 step size.
   */
  maxAdvanceBeatsPerFrame(): number {
    return Math.min(
      MAX_ADVANCE_BEATS_BASE * Math.max(1, this.getPlaybackRate()),
      HAZARD_INVULN_BEATS,
    );
  }

  /** Active loop's start/end speeds. Surfaced for tests asserting the ramp. */
  getSpeedRamp(): LoopSpeedRamp {
    const { startSpeed, endSpeed } = this.speedClock;
    return { startSpeed, endSpeed };
  }

  /**
   * Restart the music for a new loop without tearing down the audio
   * context (which would lose the user's gesture-grant and force a
   * reload). Stops the current source, schedules a fresh one from the
   * start of the file at the new {@link LoopSpeedRamp}, and resets
   * `songStartTime` so {@link getCurrentBeat} reads -`leadInBeats` at
   * the start of the new loop.
   *
   * Returns the ctx-time at which the new beat 0 lands, so the loop
   * manager can sync UI and lead-in tick scheduling.
   */
  async restartForLoop(
    chart: Chart,
    leadInBeats: number,
    speedRamp: LoopSpeedRamp,
  ): Promise<number> {
    if (!this.started) {
      throw new Error('AudioEngine.restartForLoop: engine has not been started yet');
    }
    // Stop the previous source instantly: we already paused gameplay
    // for the celebration / mode-card, so a hard cut here is fine and
    // avoids audio bleeding across the transition.
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch {
        // Already stopped — ignore.
      }
      try {
        this.musicSource.disconnect();
      } catch {
        // Already disconnected — ignore.
      }
      this.musicSource = null;
    }
    if (this.musicFadeTimer !== null) {
      clearTimeout(this.musicFadeTimer);
      this.musicFadeTimer = null;
    }

    await this.resume();
    // Restore the music bus gain in case stopMusic() ramped it to 0
    // before the celebration paused playback.
    try {
      const now = this.ctx.currentTime;
      const gain = this.musicBus.gain;
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(1, now);
    } catch (err) {
      console.warn('Failed to reset music bus gain for new loop:', err);
    }

    this.bpm = chart.bpm;
    this.applySpeedRamp(chart, speedRamp);

    const userOffsetSeconds = this.opts.audioOffsetMs / 1000;
    const leadInSeconds = this.computeLeadInSeconds(leadInBeats, chart.bpm);
    this.songStartTime = this.ctx.currentTime + 0.1 + leadInSeconds + userOffsetSeconds;

    let buffer: AudioBuffer;
    try {
      buffer = await fetchAndDecodeAudio(this.ctx, chart.audio.src);
    } catch (err) {
      console.warn('AudioEngine: failed to reload music for loop', chart.slug, err);
      return this.songStartTime;
    }
    const fileOffsetSeconds = Math.max(0, chart.audio.offsetMs / 1000);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    this.scheduleSourceSpeedRamp(source);
    source.connect(this.musicBus);
    source.start(this.songStartTime, fileOffsetSeconds);
    this.musicSource = source;
    return this.songStartTime;
  }

  scheduleHitFeedback(
    verdict: 'perfect' | 'good' | 'ok' | 'miss',
    when?: number,
    opts: HitClickOptions = {},
  ): void {
    if (!this.acquireSfxVoice()) return;
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleHitClick(this.ctx, this.sfxBus, t0, verdict, opts);
      this.releaseSfxVoiceAfter(0.2);
    } catch (err) {
      // If Web Audio threw (closed context, unsupported state) the voice
      // was never queued, so release the slot synchronously instead of
      // leaving the counter permanently elevated.
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleHitClick failed:', err);
    }
  }

  /**
   * Circles-mode hit feedback: the shared {@link scheduleHitClick}
   * baseline plus any imported osu! hitsound accents (whistle / finish
   * / clap) the struck note carries. Plain notes (no `hitSound`) sound
   * identical to {@link scheduleHitFeedback} — the accent layer is a
   * pure addition on hitsounded maps, so the base game feel is
   * untouched.
   *
   * Accents fire only on successful hits (verdict ≠ miss): osu! plays a
   * note's hitsounds on any judgement that connects, never on a miss.
   * Counts as ONE voice regardless of how many accents stack; the hold
   * extends to the longest accent tail returned by
   * {@link scheduleCirclesHitsounds} so a finish wash or long whistle
   * isn't truncated.
   */
  scheduleCirclesHitFeedback(
    verdict: 'perfect' | 'good' | 'ok' | 'miss',
    when: number | undefined,
    hitSound: number | undefined,
    hitSampleSet: HitSampleSet | undefined,
    opts: HitClickOptions = {},
  ): void {
    if (!this.acquireSfxVoice()) {
      return;
    }
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleHitClick(this.ctx, this.sfxBus, t0, verdict, opts);
      let accentTail = 0;
      if (verdict !== 'miss' && hitSound) {
        accentTail = scheduleCirclesHitsounds(
          this.ctx,
          this.sfxBus,
          t0,
          hitSound,
          hitSampleSet ?? 'normal',
          opts.pan ?? 0,
        );
      }
      // Hold the voice for the base click's own decay (0.2 s) or the
      // longest accent tail, whichever is longer, so a finish wash or a
      // long soft whistle isn't truncated when the slot is recycled.
      this.releaseSfxVoiceAfter(Math.max(0.2, accentTail));
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleCirclesHitFeedback failed:', err);
    }
  }

  /**
   * Guitar-mode enriched hit-feedback. Composes the shared
   * {@link scheduleHitClick} baseline plus mode-specific layers (chord
   * stab on dual gems, power chord on big-combo perfects, hold-release
   * ring-out, slapback echo, sub on goods) — all routed through the
   * SFX bus so the {@link enableStageBus} compressor + saturator +
   * reverb glue them together.
   *
   * Counts as ONE voice in the existing MAX_SFX_VOICES cap regardless
   * of how many sub-layers fire, so dense Guitar charts don't fall
   * off the voice ceiling faster than other modes. Voice hold extends
   * to ~0.6s when the ring-out fires so the tail isn't truncated.
   */
  scheduleGuitarHitFeedback(
    verdict: 'perfect' | 'good' | 'miss',
    when?: number,
    opts: GuitarHitLayersOptions = {},
  ): void {
    if (!this.acquireSfxVoice()) {
      return;
    }
    const t0 = when ?? this.ctx.currentTime;
    try {
      const ringingTail = scheduleGuitarHitLayers(this.ctx, this.sfxBus, t0, verdict, opts);
      this.releaseSfxVoiceAfter(ringingTail ? 0.6 : 0.25);
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleGuitarHitFeedback failed:', err);
    }
  }

  /**
   * Splice the Guitar stage bus (compressor + soft saturator + reverb
   * send) between the SFX bus and master. Idempotent — calling twice
   * does nothing on the second call. Guitar sessions call this from
   * their constructor; Piano + Circles leave it off so their SFX
   * paths stay dry.
   */
  enableStageBus(): void {
    if (this.guitarStageBus) {
      return;
    }
    try {
      this.sfxBus.disconnect(this.master);
    } catch (err) {
      // Web Audio's `disconnect(node)` throws if the connection
      // doesn't exist — proceeding would double-route the SFX bus
      // (one path through master, one through the stage bus). Bail
      // and leave the dry path intact.
      console.warn('AudioEngine.enableStageBus: sfxBus disconnect failed, bailing:', err);
      return;
    }
    let lowpass: BiquadFilterNode | null = null;
    try {
      this.musicBus.disconnect(this.master);
      lowpass = this.ctx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = MUSIC_LOWPASS_NEUTRAL_HZ;
      lowpass.Q.value = 0.7;
      this.musicBus.connect(lowpass).connect(this.master);
      const bus = new GuitarStageBus(this.ctx, this.master);
      this.sfxBus.connect(bus.getInput());
      this.guitarStageBus = bus;
      this.musicLowpass = lowpass;
    } catch (err) {
      // Stage bus or lowpass splice threw — restore the dry path so
      // SFX + music still route through to the master rather than
      // being silently dropped.
      console.warn('AudioEngine.enableStageBus failed; falling back to dry path:', err);
      if (lowpass) {
        try {
          lowpass.disconnect();
        } catch (innerErr) {
          console.warn('AudioEngine.enableStageBus: lowpass cleanup failed:', innerErr);
        }
      }
      try {
        this.musicBus.connect(this.master);
      } catch (innerErr) {
        console.warn('AudioEngine.enableStageBus: music dry-path restore failed:', innerErr);
      }
      try {
        this.sfxBus.connect(this.master);
      } catch (innerErr) {
        console.warn('AudioEngine.enableStageBus: sfx dry-path restore failed:', innerErr);
      }
    }
  }

  /**
   * Released on loop boundaries so the next loop starts crisp without
   * the previous loop's reverb tail bleeding into the lead-in. Also
   * resets the music lowpass to neutral (in case a crash sweep was
   * mid-flight) and tears down any held drones still sustaining.
   * No-op for the stage bus when it isn't engaged.
   */
  clearGuitarAudioFx(): void {
    if (this.guitarStageBus) {
      this.guitarStageBus.silenceReverbTail();
    }
    this.stopAllGuitarHoldDrones();
    if (!this.musicLowpass) {
      return;
    }
    try {
      const now = this.ctx.currentTime;
      const cutoff = this.musicLowpass.frequency;
      cutoff.cancelScheduledValues(now);
      cutoff.setValueAtTime(MUSIC_LOWPASS_NEUTRAL_HZ, now);
    } catch (err) {
      console.warn('AudioEngine.clearGuitarAudioFx: lowpass reset failed:', err);
    }
  }

  /**
   * Heavier multi-miss "crash" boom — saturated mid pitch-drop +
   * stuttering noise cluster on top of the regular miss click.
   * Reserved for the Guitar session's multi-miss crash path so the
   * player viscerally feels they're crashing the run, not just
   * fumbling a single note.
   */
  scheduleCrashBoom(when?: number): void {
    if (!this.acquireSfxVoice()) {
      return;
    }
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleCrashBoom(this.ctx, this.sfxBus, t0);
      this.releaseSfxVoiceAfter(0.55);
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleCrashBoom failed:', err);
    }
  }

  /**
   * Sweep the music lowpass cutoff DOWN to `toFreq` over `attackMs`,
   * hold for `sustainMs`, then ramp back to neutral over `releaseMs`.
   * Layered with {@link duckMusicPulse} for the Guitar multi-miss
   * crash so the music both dims AND loses its top end — the "amp
   * blown out" effect — rather than just attenuating.
   *
   * Idempotent: subsequent calls cancel pending ramps and restart
   * from the current cutoff so back-to-back crashes don't compound
   * into total mid-frequency cancellation.
   */
  sweepMusicLowpass(toFreq: number, attackMs: number, sustainMs: number, releaseMs: number): void {
    if (!this.musicLowpass) {
      return;
    }
    const now = this.ctx.currentTime;
    const attack = Math.max(0.005, attackMs / 1000);
    const sustain = Math.max(0, sustainMs / 1000);
    const release = Math.max(0.01, releaseMs / 1000);
    const safeTo = Math.max(120, Math.min(MUSIC_LOWPASS_NEUTRAL_HZ, toFreq));
    try {
      const cutoff = this.musicLowpass.frequency;
      const startValue = Math.max(cutoff.value, 120);
      cutoff.cancelScheduledValues(now);
      cutoff.setValueAtTime(startValue, now);
      cutoff.exponentialRampToValueAtTime(safeTo, now + attack);
      cutoff.setValueAtTime(safeTo, now + attack + sustain);
      cutoff.exponentialRampToValueAtTime(MUSIC_LOWPASS_NEUTRAL_HZ, now + attack + sustain + release);
    } catch (err) {
      console.warn('sweepMusicLowpass failed:', err);
    }
  }

  /**
   * Guitar-mode milestone fanfare — the shared chord + bell wrapped
   * with a build-up riser sweep and a crowd-cheer swell so a star /
   * cup celebration sounds like a venue erupting. Tier-aware (3 is
   * the loudest cheer). Bypasses the SFX cap because milestones are
   * infrequent, single-shot events that absolutely must play.
   */
  playGuitarMilestoneFanfare(tier: 1 | 2 | 3, when?: number): void {
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleGuitarMilestoneFanfare(this.ctx, this.sfxBus, t0, tier);
    } catch (err) {
      this.scheduleErrors += 1;
      console.warn('scheduleGuitarMilestoneFanfare failed:', err);
    }
  }

  /**
   * Begin a wammy-style sustain drone for `lane` for as long as the
   * player holds the corresponding note. Idempotent for the same
   * lane: if a drone is already running we tear it down first so
   * the new one starts clean (covers the engine-rare case of a
   * stale drone outlasting its hold). Routed through the SFX bus so
   * the stage bus glues it under the chord stab / hit click.
   */
  startGuitarHoldDrone(lane: number, opts: GuitarHoldDroneOptions = {}): void {
    const existing = this.guitarHoldDrones.get(lane);
    if (existing) {
      existing.destroy();
      this.guitarHoldDrones.delete(lane);
    }
    try {
      const drone = new GuitarHoldDrone(this.ctx, this.sfxBus, lane, opts);
      this.guitarHoldDrones.set(lane, drone);
    } catch (err) {
      this.scheduleErrors += 1;
      console.warn('startGuitarHoldDrone failed:', err);
    }
  }

  /**
   * End the drone for `lane` with a short fade-out. No-op when no
   * drone is active for the lane (covers the engine's auto-release
   * path firing before the session's reconciliation tick).
   */
  stopGuitarHoldDrone(lane: number): void {
    const drone = this.guitarHoldDrones.get(lane);
    if (!drone) {
      return;
    }
    this.guitarHoldDrones.delete(lane);
    try {
      drone.stop();
    } catch (err) {
      console.warn('stopGuitarHoldDrone failed:', err);
    }
  }

  /**
   * Tear down every active hold drone immediately. Called by
   * {@link clearGuitarAudioFx} on loop boundaries and by
   * {@link stop} during session teardown — both want a hard cut
   * rather than a fade-out tail bleeding into silence.
   */
  stopAllGuitarHoldDrones(): void {
    if (this.guitarHoldDrones.size === 0) {
      return;
    }
    for (const drone of this.guitarHoldDrones.values()) {
      try {
        drone.destroy();
      } catch (err) {
        console.warn('stopAllGuitarHoldDrones: destroy failed:', err);
      }
    }
    this.guitarHoldDrones.clear();
  }

  /**
   * Tick used by the score-roll-up animation. Bypasses the SFX cap
   * because it's quiet, very short (~45 ms), and bursts of them are the
   * intended UX — capping them would make a fast roll silently drop
   * later ticks and feel laggy. The tick tone is too thin to dominate
   * the mix even when several stack.
   */
  scheduleScoreTick(when?: number): void {
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleScoreTick(this.ctx, this.sfxBus, t0);
    } catch (err) {
      this.scheduleErrors += 1;
      console.warn('scheduleScoreTick failed:', err);
    }
  }

  /**
   * Big-combo break: the regular miss click PLUS a deeper boom layered on
   * top. Reserved for combos of ~25+ so it really feels like an event.
   */
  scheduleComboBreak(when?: number): void {
    if (!this.acquireSfxVoice()) return;
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleComboBreakBoom(this.ctx, this.sfxBus, t0);
      this.releaseSfxVoiceAfter(0.4);
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleComboBreakBoom failed:', err);
    }
  }

  scheduleFlip(when?: number): void {
    if (!this.acquireSfxVoice()) return;
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleFlipWhoosh(this.ctx, this.sfxBus, t0);
      this.releaseSfxVoiceAfter(0.2);
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleFlipWhoosh failed:', err);
    }
  }

  /** Ascending thrust blip on press; descending dive blip on release. */
  scheduleWavesEdge(pressing: boolean, when?: number): void {
    if (!this.acquireSfxVoice()) {
      return;
    }
    const t0 = when ?? this.ctx.currentTime;
    try {
      if (pressing) {
        scheduleWavesThrust(this.ctx, this.sfxBus, t0);
      } else {
        scheduleWavesDive(this.ctx, this.sfxBus, t0);
      }
      this.releaseSfxVoiceAfter(0.12);
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleWavesEdge failed:', err);
    }
  }

  /**
   * Burst-chamber tap blip. Voiced through the same MAX_SFX_VOICES cap
   * as the rest so a mash session doesn't spawn dozens of overlapping
   * AudioNodes per frame; if all slots are taken we silently drop the
   * tap (the visual + score still register).
   */
  scheduleMashTap(when?: number): void {
    if (!this.acquireSfxVoice()) return;
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleMashTap(this.ctx, this.sfxBus, t0);
      this.releaseSfxVoiceAfter(0.08);
    } catch (err) {
      this.releaseSfxVoiceNow();
      this.scheduleErrors += 1;
      console.warn('scheduleMashTap failed:', err);
    }
  }

  playDeath(): void {
    // Death sound is loud + emphatic; bypass the SFX cap so it always
    // plays (the cap exists for *repeated* one-shots, not single events).
    scheduleDeathSound(this.ctx, this.sfxBus, this.ctx.currentTime);
  }

  scheduleLeadInTicks(beats: number): void {
    // Skip ticks whose ideal time has already passed (e.g. negative audio
    // offset). Clamping would stack multiple ticks on the same instant and
    // produce an audibly louder/distorted blip.
    const minTime = this.ctx.currentTime + 0.005;
    // Accelerated loops pack many beats into the fixed-wall-time pre-roll;
    // thinning sub-perceptual ticks keeps the count-in audible instead of a
    // wall of overlapping oscillators. The final "GO" tick skips the gap
    // check (it still honours the past-time guard above).
    const minTickGapSeconds = 0.08;
    let lastTickTime = -Infinity;
    for (let i = 0; i < beats; i++) {
      const t = this.beatToCtxTime(i - beats);
      if (t < minTime) {
        continue;
      }
      const isFinal = i === beats - 1;
      if (!isFinal && t - lastTickTime < minTickGapSeconds) {
        continue;
      }
      lastTickTime = t;
      try {
        scheduleLeadInTick(this.ctx, this.sfxBus, t, isFinal);
      } catch (err) {
        this.scheduleErrors += 1;
        console.warn('scheduleLeadInTick failed:', err);
      }
    }
  }

  scheduleComboMilestone(when?: number): void {
    const t0 = when ?? this.ctx.currentTime;
    scheduleComboCelebration(this.ctx, this.sfxBus, t0);
  }

  /**
   * Star/cup milestone fanfare. Tier 1/2/3 controls the chord size.
   * Bypasses the SFX cap because milestones are infrequent, single-shot
   * events that absolutely must play even if hit clicks are saturating.
   */
  playMilestoneFanfare(tier: 1 | 2 | 3, when?: number): void {
    const t0 = when ?? this.ctx.currentTime;
    try {
      scheduleMilestoneFanfare(this.ctx, this.sfxBus, t0, tier);
    } catch (err) {
      this.scheduleErrors += 1;
      console.warn('scheduleMilestoneFanfare failed:', err);
    }
  }

  /** Soften the music bus quickly (for the loop-1 finale overlay). */
  duckMusic(toGain = 0.15, ms = 250): void {
    if (!this.musicSource) return;
    const now = this.ctx.currentTime;
    const ramp = Math.max(0.01, ms / 1000);
    try {
      const gain = this.musicBus.gain;
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(Math.max(gain.value, 0.0001), now);
      gain.linearRampToValueAtTime(Math.max(0.0001, toGain), now + ramp);
    } catch (err) {
      console.warn('duckMusic failed:', err);
    }
  }

  /**
   * Pulse-duck: ramp the music bus DOWN to `dipGain` then ramp back
   * to 1.0. Used for short "you lost something" moments (miss / combo
   * break) where the music dims for a beat to let the SFX cut through
   * and then swells back.
   *
   * Idempotent and immediate: subsequent calls cancel pending ramps
   * and start fresh from whatever gain the bus is at right now, so
   * back-to-back misses don't compound into total silence.
   */
  duckMusicPulse(dipGain: number, attackMs: number, releaseMs: number): void {
    if (!this.musicSource) return;
    const now = this.ctx.currentTime;
    const attack = Math.max(0.005, attackMs / 1000);
    const release = Math.max(0.005, releaseMs / 1000);
    const safeDip = Math.max(0.001, Math.min(1, dipGain));
    try {
      const gain = this.musicBus.gain;
      const startValue = Math.max(gain.value, 0.0001);
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(startValue, now);
      gain.linearRampToValueAtTime(safeDip, now + attack);
      gain.linearRampToValueAtTime(1.0, now + attack + release);
    } catch (err) {
      console.warn('duckMusicPulse failed:', err);
    }
  }

  playVictorySting(): void {
    scheduleVictorySting(this.ctx, this.sfxBus, this.ctx.currentTime);
  }

  playFailureSting(): void {
    scheduleFailureSting(this.ctx, this.sfxBus, this.ctx.currentTime);
  }

  /**
   * Halt the song without tearing down the audio context. Used at end-of-run
   * (clear or death) so the music doesn't keep playing under the results
   * overlay while the SFX bus stays alive for the victory/failure sting.
   * A short exponential fade avoids the click that a hard `source.stop()`
   * produces mid-waveform.
   */
  stopMusic(fadeMs = 180): void {
    if (!this.musicSource) {
      return;
    }
    const source = this.musicSource;
    this.musicSource = null;
    const now = this.ctx.currentTime;
    const fadeS = Math.max(0.01, fadeMs / 1000);
    try {
      const gain = this.musicBus.gain;
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(Math.max(gain.value, 0.0001), now);
      gain.exponentialRampToValueAtTime(0.0001, now + fadeS);
    } catch (err) {
      console.warn('Failed to schedule music fade-out:', err);
    }
    try {
      source.stop(now + fadeS + 0.02);
    } catch {
      // Already stopped (e.g. song ended naturally) - nothing to do.
    }
    // Disconnect after the source has finished its fade-out. Disconnecting
    // synchronously would sever the source -> bus path and cut the audio
    // mid-fade (the bus would have nothing to fade), producing the click we
    // scheduled the fade to avoid.
    const disconnectAt = (fadeS + 0.05) * 1000;
    this.musicFadeTimer = setTimeout(() => {
      this.musicFadeTimer = null;
      try {
        source.disconnect();
      } catch {
        // Already disconnected (e.g. context closed) - nothing to do.
      }
    }, disconnectAt);
  }

  /**
   * Try to take an SFX voice slot. Returns false (caller should bail out)
   * when too many one-shots are already in flight, which prevents Space
   * mash from spawning hundreds of overlapping AudioNodes.
   */
  private acquireSfxVoice(): boolean {
    if (this.activeSfx >= MAX_SFX_VOICES) return false;
    this.activeSfx += 1;
    return true;
  }

  private releaseSfxVoiceAfter(seconds: number): void {
    let timer: TimeoutId | undefined;
    timer = setTimeout(() => {
      this.activeSfx = Math.max(0, this.activeSfx - 1);
      if (timer !== undefined) this.pendingSfxReleases.delete(timer);
    }, seconds * 1000);
    this.pendingSfxReleases.add(timer);
  }

  /** Drop the slot immediately - used when scheduling threw and never queued. */
  private releaseSfxVoiceNow(): void {
    this.activeSfx = Math.max(0, this.activeSfx - 1);
  }

  async stop(): Promise<void> {
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch {
        // Already stopped or never started - ignore.
      }
      try {
        this.musicSource.disconnect();
      } catch {
        // Already disconnected - ignore.
      }
      this.musicSource = null;
    }
    // Clear any in-flight SFX release timers so they don't fire on a
    // dead engine. Their AudioNodes have already been queued in the
    // context; closing the context kills them.
    for (const t of this.pendingSfxReleases) clearTimeout(t);
    this.pendingSfxReleases.clear();
    this.activeSfx = 0;
    if (this.musicFadeTimer !== null) {
      clearTimeout(this.musicFadeTimer);
      this.musicFadeTimer = null;
    }
    this.stopAllGuitarHoldDrones();
    if (this.guitarStageBus) {
      this.guitarStageBus.destroy();
      this.guitarStageBus = null;
    }
    if (this.musicLowpass) {
      try {
        this.musicLowpass.disconnect();
      } catch (err) {
        console.warn('Failed to disconnect musicLowpass:', err);
      }
      this.musicLowpass = null;
    }
    try {
      await this.ctx.close();
    } catch (err) {
      console.warn('Failed to close audio context:', err);
    }
  }
}
